import {type List} from 'immutable';
import {type ComponentType} from 'react';
import {type css} from 'styled-components';

import {type NonEmptyArray} from 'web-app/util/non-empty-array';
import {type ButtonInputProps} from 'web-app/react/components/button-v2';

import {type ImportApiProps} from './import';

//
// Util
//

export type SpreadsheetRow = Array<string | undefined>;
export type SpreadsheetData = SpreadsheetRow[];

//
// Modelling for converting rows to previews and observables
//
export interface CreationModel<Types> {
    type: Types;
    data: any; // :(
    rowIndex?: number;
    importObjectId?: string;
}
export interface ReadableCreationDescription {
    title: string;
    description: number | string;
}
export type CreationPreviewInput = ReadableCreationDescription[];
export type OrderedCreationList<Types> = Array<Array<CreationModel<Types>>>;

//
// Config
//
export enum ColumnDataType {
    string = 'string',
    date = 'date',
    email = 'email',
}
export interface Mapping {
    value: string;
    label: string;
}

export type ValueMapping = {
    [x: string]: Mapping | undefined;
};

export interface DefaultMapping {
    mappingValue: Mapping['value'];
    cellValue: string;
}

export interface Choice {
    value: string;
    label: string;
}
export interface ColumnConfig<CKey> {
    key: CKey;
    label: string;
    /*
        If this is added to a columnCfg it will be used to scan through the first row of a spreadsheet
        and if a match is found it's automatically applied. Otherwise the label is used.
     */
    matchLabel?: string;
    entityLabel?: string;
    dataType: ColumnDataType;
    required?: boolean;
    excludes?: CKey[];
    requires?: CKey[];
    mappings?: Mapping[];
    defaultMappings?: DefaultMapping[];
    choices?: Choice[];
    canCreateChoice?: boolean;
    defaultDateFormat?: string;
    locked?: boolean;
    isRequiredAndMissingText?: string;
}

export type ColumnConfigs<CKeys> = List<ColumnConfig<CKeys>>;

/*
    This is the external interface of the generic import module.
    The type parameters you have to supply are the keys of your user-selectable columns (CKeys) and the keys
    of what you're going to create as a result of the import (CreationTypes)
 */

export type SubmitHandler<CreationTypes> = (
    orderedCreationList: OrderedCreationList<CreationTypes>,
    remainingRows: SpreadsheetData,
    importApi: ImportApiProps,
) => void;

export type SubmitHandlerConfig<CreationTypes> = Pick<ButtonInputProps, 'text' | 'icon' | 'appearance'> & {
    onClick: SubmitHandler<CreationTypes>;
    includeInvalidRows?: boolean;
};

export interface ImportConfig<CKeys extends string, CreationTypes> {
    lockColumns?: boolean;
    /*
        `columns` corresponds to the options users are able to select in the drop-down at the top of the table.
     */
    columns: ColumnConfigs<CKeys>;
    /*
        `exampleMapper` will give you a row that's been transformed through the user defined column configuration
        and you are then able to return a layout to present the values.
        The internals will handle stuff like validation, invalid dates, missing required values etc.
        This is purely a description of layout
     */
    exampleMapper: (entity: BaseMappedRow<CKeys>) => ExampleView<CKeys>;
    /*
        `makeExampleTitle` is a small convenience function which is used to create the title for the rendered example
     */
    makeExampleTitle: (rowNumber: number | null, mappedRow: BaseMappedRow<string>) => string | null;
    /*
        The concept of `OrderedCreationList` is to create an intermediate model describing what we will create and
        in what order. The function takes all of the user configured rows, and implementations will then have to
        supply this ordered list of what should be created. This is basically a step between submit is started and
        API calls are made.

        e.g. for child import we first want to create rooms, and only after all these are finished will we start
        adding children.
     */
    makeOrderedCreationList: (entities: Array<MappedRow<CKeys>>) => OrderedCreationList<CreationTypes>;
    /*
        `creationListToPreview` currently no internals are implemented to make use of this.
        The idea is to generate a layout that will show what's about to be created as effect of the
        imported spreadsheet and how it was configured.
     */
    creationListToPreview?: (orderedCreationList: OrderedCreationList<CreationTypes>) => CreationPreviewInput;

    create: SubmitHandler<CreationTypes>;

    customSubmitHandlers?: NonEmptyArray<SubmitHandlerConfig<CreationTypes>>;
    customSubmitContainerCSS?: ReturnType<typeof css>;
    hideExampleRow?: boolean;
    requiredFieldsExplanation?: string;
}

export type BaseMappedRow<CKeys extends string> = {[CKey in CKeys]?: string | Mapping};
export type MappedRow<CKeys extends string> = [number, BaseMappedRow<CKeys>];

export type Mapper<CKeys extends string> = (
    index: number,
    row: SpreadsheetRow,
    columnsConfigs: ColumnConfigs<CKeys>,
    configKeys: List<CKeys | undefined>,
    columnMappings: ColumnMappings<CKeys>,
) => MappedRow<CKeys>;

//
// Example view
//

export interface ExampleRow<CKey extends string> {
    key: CKey;
    label: string;
    value?: string;
}

export interface ExampleColumn<CKeys extends string> {
    title?: string;
    rows: Array<ExampleRow<CKeys>>;
}

export interface ExampleView<CKeys extends string> {
    columns: Array<ExampleColumn<CKeys>>;
}

export type ColumnMappings<CKeys extends string> = {
    [CKey in CKeys]: ValueMapping;
};

export type RowMappings<CKeys extends string> = Array<ColumnMappings<CKeys>>;

export type ColumnDates<CKeys extends string> = {[CKey in CKeys]: string};

export enum RowToggleState {
    Enabled = 'Enabled',
    Disabled = 'Disabled',
}

export enum ImportModalState {
    Hidden,
    Importing,
    ImportFinished,
}

export enum CreationType {
    Group = 'Group',
    Child = 'Child',
    BillPayer = 'BillPayer',
}

export interface ImportResult {
    type: CreationType;
    success: boolean;
    count: number;
}

export type ColumnMappingsLabelByValue = {[value: string]: string};

export type DataSourceComponent<P> = ComponentType<
    React.PropsWithChildren<P & {loadSpreadsheetData: (data: SpreadsheetData) => void}>
>;
export type ToolbarComponent<P> = ComponentType<React.PropsWithChildren<P>>;
export type FinishComponent<P> = ComponentType<React.PropsWithChildren<P>>;

export type OptionalToolbarProps<P extends {}> =
    | {
          toolbarComponent: ToolbarComponent<P>;
          toolbarProps: P;
      }
    | {
          toolbarComponent?: undefined;
          toolbarProps?: undefined;
      };

export type FinishComponentProps<P extends {} | undefined> = {
    finishComponent?: FinishComponent<P & {reset: () => void}>;
    finishProps?: P;
};

export type OptionalTitleProp =
    | {
          show: false;
      }
    | {
          show: true;
          content: string;
      };

export type SpreadsheetOptionalTitleProps = {
    titles?: {
        chooseTitle?: OptionalTitleProp;
        chooseSubtitle?: OptionalTitleProp;
        importTitle?: OptionalTitleProp;
    };
};
